import React, {useCallback, useEffect, useState} from 'react';
import clsx from 'clsx';
import {Time} from '@internationalized/date';
import type {TimeValue} from 'react-aria';
import {TimeRecord, TimeRecordEntryType, TimeRecordStatus} from '../../../api/TimetrackingApi';
import {TimeRecordEntrySnapshot, TimeRecordRequestCreateRequest} from '../../../api/RequestApi';

import {Modal, ModalActions, ModalBody} from '../../../components/Modal';
import {Button} from '../../../components/Button';
import {IconButton} from '../../../components/IconButton';
import Typography from '../../../components/Typography';
import {TimeRangeField} from '../../../components/TimeRangeField';
import {DeleteIcon, PlusIcon} from '../../../components/Icons';
import {DateUtils} from "../../../util/DateUtils";
import {Workday} from "../../../api/WorkDayRecordApi";


interface EditEntry {
    /** Stable local key for React – not sent to the backend. */
    _key: string;
    entryType: TimeRecordEntryType;
    /** HH:mm — matches what <input type="time"> produces */
    startTime: string;
    /** HH:mm — empty string means the entry is still open */
    endTime: string;
}

export interface TimeRecordEditModalProps {
    open: boolean;
    onClose: () => void;
    /** ISO date string YYYY-MM-DD for the day being edited. */
    date: Date;
    /** Existing record, if any. Used to pre-populate entries. */
    workday: Workday;
    /** Called on submit with the correction request payload (without employeeId). */
    onSubmit: (data: Omit<TimeRecordRequestCreateRequest, 'employeeId'>) => Promise<void>;
    /** True while the parent is persisting the request. */
    submitting: boolean;
}

const ENTRY_TYPES: TimeRecordEntryType[] = ['WORK', 'BREAK', 'ABSENCE'];

const ENTRY_TYPE_LABELS: Record<TimeRecordEntryType, string> = {
    WORK: 'Arbeit',
    BREAK: 'Pause',
    ABSENCE: 'Abwesenheit',
};

/** Records in these statuses cannot be revised. */
const NON_EDITABLE_STATUSES: TimeRecordStatus[] = [
    TimeRecordStatus.LOCKED,
    TimeRecordStatus.EXPORTED,
];

let _keyCounter = 0;

function nextKey(): string {
    return String(++_keyCounter);
}

/** "HH:mm:ss" or "HH:mm:ss.SSS" → "HH:mm" */
function toHHMM(time: string): string {
    return time ? time.substring(0, 5) : '';
}

/** "HH:mm" → Time | null */
function hhmmToTimeValue(hhmm: string): TimeValue | null {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return new Time(h, m);
}

/** TimeValue → "HH:mm" */
function timeValueToHHMM(tv: TimeValue | null): string {
    if (!tv) return '';
    return `${String(tv.hour).padStart(2, '0')}:${String(tv.minute).padStart(2, '0')}`;
}

function formatDayLabel(isoDate: Date): string {
    return isoDate.toLocaleDateString('de-DE', {weekday: 'long', day: 'numeric', month: 'long'});
}

function buildEntriesFromWorkday(workday: Workday | undefined): EditEntry[] {
    if (!workday) return [];

    // Correction takes priority over the base time record
    if (workday.correction?.entries?.length) {
        return workday.correction.entries.map((e) => ({
            _key: nextKey(),
            entryType: e.entryType,
            startTime: toHHMM(e.startTimestamp),
            endTime: e.endTimestamp ? toHHMM(e.endTimestamp) : '',
        }));
    }

    // Fall back to the base time record (if present and not projection-only).
    if (!workday.projectionOnly && workday.timeRecord?.entries?.length) {
        return workday.timeRecord.entries.map((e) => ({
            _key: nextKey(),
            entryType: (e.entryType as TimeRecordEntryType) ?? 'WORK',
            // startTimestamp is a full UTC ISO string — use the pre-converted local wall-clock time
            startTime: toHHMM(e.localStartTime),
            endTime: e.open ? '' : toHHMM(e.localEndTime),
        }));
    }

    // Projection-only or no data — start with an empty slate
    return [];
}

function validateEntries(entries: EditEntry[]): Record<string, string> {
    const errors: Record<string, string> = {};

    entries.forEach((e, i) => {
        if (!e.startTime) {
            errors[`${i}_start`] = 'Startzeit erforderlich';
        }
        if (e.endTime && e.startTime && e.endTime <= e.startTime) {
            errors[`${i}_end`] = 'Endzeit muss nach Startzeit liegen';
        }
    });

    // Overlap check — sort a copy by startTime, then compare each consecutive pair
    const sorted = entries
        .map((e, originalIndex) => ({...e, originalIndex}))
        .filter((e) => e.startTime)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));

    for (let i = 0; i < sorted.length - 1; i++) {
        const current = sorted[i];
        const next = sorted[i + 1];

        if (!current.endTime) {
            // Open entry followed by another entry — overlap by definition
            errors[`${current.originalIndex}_end`] =
                `Eintrag ist noch offen, aber es folgt ein weiterer Eintrag ab ${next.startTime}`;
            continue;
        }

        if (current.endTime > next.startTime) {
            errors[`${current.originalIndex}_end`] =
                `Überschneidung: ${current.startTime}–${current.endTime} überschneidet sich mit ${next.startTime}–${next.endTime || 'offen'}`;
            errors[`${next.originalIndex}_start`] =
                `Überschneidung mit vorherigem Eintrag (${current.startTime}–${current.endTime})`;
        }
    }

    return errors;
}

/** Maps an EditEntry to the immutable snapshot sent to the backend. */
function toEntrySnapshot(entry: EditEntry): TimeRecordEntrySnapshot {
    return {
        entryType: entry.entryType,
        startTimestamp: entry.startTime,
        endTimestamp: entry.endTime || null,
    };
}

interface TypeToggleProps {
    value: TimeRecordEntryType;
    onChange: (type: TimeRecordEntryType) => void;
    isDisabled?: boolean;
}

const TypeToggle: React.FC<TypeToggleProps> = ({value, onChange, isDisabled}) => (
    <div className="flex rounded-md border border-gray-300 overflow-hidden flex-shrink-0">
        {ENTRY_TYPES.map((type, i) => (
            <button
                key={type}
                type="button"
                disabled={isDisabled}
                onClick={() => onChange(type)}
                className={clsx(
                    'px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-inset',
                    i > 0 && 'border-l border-gray-300',
                    value === type
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100',
                    isDisabled && 'opacity-50 cursor-not-allowed',
                )}
            >
                {ENTRY_TYPE_LABELS[type]}
            </button>
        ))}
    </div>
);

interface EntryRowProps {
    entry: EditEntry;
    index: number;
    isDisabled: boolean;
    error: { start?: string; end?: string };
    onChange: (key: string, patch: Partial<EditEntry>) => void;
    onDelete: (key: string) => void;
}

const EntryRow: React.FC<EntryRowProps> = ({entry, index, isDisabled, error, onChange, onDelete}) => {
    const handleTimeChange = useCallback(
        (start: TimeValue | null, end: TimeValue | null) => {
            onChange(entry._key, {
                startTime: timeValueToHHMM(start),
                endTime: timeValueToHHMM(end),
            });
        },
        [entry._key, onChange],
    );

    return (
        <div
            className={clsx(
                'flex items-start gap-3 py-3',
                'border-b border-gray-100 last:border-b-0',
            )}
        >
            {/* Row index */}
            <Typography
                variant="caption"
                color="secondary"
                className="pt-2 w-4 flex-shrink-0 text-center select-none"
            >
                {index + 1}
            </Typography>

            {/* Entry type toggle */}
            <TypeToggle
                value={entry.entryType}
                isDisabled={isDisabled}
                onChange={(type) => onChange(entry._key, {entryType: type})}
            />

            {/* Time range */}
            <div className="flex-1 min-w-0">
                <TimeRangeField
                    startValue={hhmmToTimeValue(entry.startTime)}
                    endValue={hhmmToTimeValue(entry.endTime)}
                    onChange={handleTimeChange}
                    isDisabled={isDisabled}
                    hourCycle={24}
                />
                {(error.start || error.end) && (
                    <p className="field-error mt-1">
                        {error.start || error.end}
                    </p>
                )}
                {!error.end && entry.endTime === '' && (
                    <p className="mt-1 text-xs text-gray-400">Offen</p>
                )}
            </div>

            {/* Delete */}
            <div className="pt-0.5 flex-shrink-0">
                <IconButton
                    size="small"
                    variant="ghost"
                    color="error"
                    isDisabled={isDisabled}
                    onPress={() => onDelete(entry._key)}
                    aria-label="Eintrag entfernen"
                >
                    <DeleteIcon/>
                </IconButton>
            </div>
        </div>
    );
};

export const WorkDayRecordEditModal: React.FC<TimeRecordEditModalProps> = ({
                                                                               open,
                                                                               onClose,
                                                                               workday,
                                                                               onSubmit,
                                                                               submitting,
                                                                           }) => {

    const [entries, setEntries] = useState<EditEntry[]>([]);
    const [comment, setComment] = useState('');
    const [serverError, setServerError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const isProjectionOnly = workday.projectionOnly;
    const timeRecord = workday.timeRecord;

    // Re-initialise form whenever the modal opens for a (potentially new) day
    useEffect(() => {
        if (!open) return;
        setEntries(buildEntriesFromWorkday(workday));
        setComment(timeRecord?.notes ?? '');
        setServerError(null);
        setValidationErrors({});
    }, [open, workday]);

    const isNonEditable = timeRecord ? NON_EDITABLE_STATUSES.includes(timeRecord.status) : false;
    const isDisabled = isNonEditable || submitting;

    // ── Entry mutations ───────────────────────────────────────────────────────

    const handleChange = useCallback((key: string, patch: Partial<EditEntry>) => {
        setEntries((prev) => prev.map((e) => (e._key === key ? {...e, ...patch} : e)));
        setValidationErrors({});
    }, []);

    const handleDelete = useCallback((key: string) => {
        setEntries((prev) => prev.filter((e) => e._key !== key));
    }, []);

    const handleAdd = useCallback(() => {
        setEntries((prev) => {
            const last = prev[prev.length - 1];
            const defaultStart = last?.endTime || '08:00';
            return [
                ...prev,
                {
                    _key: nextKey(),
                    entryType: 'WORK',
                    startTime: defaultStart,
                    endTime: '',
                },
            ];
        });
    }, []);

    // ── Save ──────────────────────────────────────────────────────────────────

    const handleSave = useCallback(async () => {
        const errors = validateEntries(entries);
        if (Object.keys(errors).length) {
            setValidationErrors(errors);
            return;
        }

        setServerError(null);

        try {
            const sorted = [...entries].sort((a, b) => a.startTime.localeCompare(b.startTime));

            await onSubmit({
                recordDate: workday.workdayDate,
                entries: sorted.map(toEntrySnapshot),
                notes: comment.trim() || undefined,
            });
        } catch (err: any) {
            console.error('Failed to submit correction request:', err);
            setServerError(err?.message ?? 'Einreichen fehlgeschlagen. Bitte erneut versuchen.');
        }
    }, [entries, comment, onSubmit]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Modal
            isOpen={open}
            onClose={onClose}
            size="xl"
            title={isProjectionOnly ? 'Neuer Zeiteintrag' : 'Zeiterfassung bearbeiten'}
        >
            {/* Subtitle */}
            <Typography variant="body2" color="secondary" className="mb-4">
                {DateUtils.formatDate(new Date(workday.workdayDate), {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })}
            </Typography>

            <ModalBody className="py-0">
                {/* Non-editable warning */}
                {isNonEditable && (
                    <div className="mb-4 px-3 py-2.5 rounded-md bg-amber-50 border border-amber-200">
                        <Typography variant="body2" color="warning">
                            Dieser Eintrag ist gesperrt oder bereits exportiert und kann nicht
                            bearbeitet werden.
                        </Typography>
                    </div>
                )}

                {/* ── Entry list ────────────────────────────────────────────── */}
                <section className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                        <Typography variant="subtitle2">Einträge</Typography>
                        <Typography variant="caption" color="secondary">
                            {entries.length} {entries.length === 1 ? 'Eintrag' : 'Einträge'}
                        </Typography>
                    </div>

                    {entries.length === 0 ? (
                        <div
                            className="py-6 flex items-center justify-center rounded-md border border-dashed border-gray-200">
                            <Typography variant="body2" color="secondary">
                                Keine Einträge. Füge unten einen hinzu.
                            </Typography>
                        </div>
                    ) : (
                        <div className="rounded-md border border-gray-200 px-3">
                            {entries.map((entry, index) => (
                                <EntryRow
                                    key={entry._key}
                                    entry={entry}
                                    index={index}
                                    isDisabled={isDisabled}
                                    error={{
                                        start: validationErrors[`${index}_start`],
                                        end: validationErrors[`${index}_end`],
                                    }}
                                    onChange={handleChange}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}

                    <div className="mt-2 flex items-center gap-2">
                        <IconButton
                            size="small"
                            variant="outlined"
                            onPress={handleAdd}
                            isDisabled={isDisabled}
                            aria-label="Eintrag hinzufügen"
                        >
                            <PlusIcon/>
                        </IconButton>
                        <Typography component="span" variant="caption" color="secondary">
                            Eintrag hinzufügen
                        </Typography>
                    </div>
                </section>

                <hr className="border-gray-100 mb-5"/>

                {/* ── Comment ──────────────────────────────────────────────── */}
                <div className="mb-4">
                    <label className={clsx('field-label', isDisabled && 'field-disabled')}>
                        Kommentar (optional)
                    </label>
                    <div className={clsx('field-container', isDisabled && 'field-disabled')}>
                        <textarea
                            rows={2}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            disabled={isDisabled}
                            className="field-input resize-none w-full"
                        />
                    </div>
                </div>

                {/* ── Server error ──────────────────────────────────────────── */}
                {serverError && (
                    <div className="mt-3">
                        <Typography variant="body2" color="error">
                            {serverError}
                        </Typography>
                    </div>
                )}
            </ModalBody>

            <ModalActions>
                <Button
                    variant="outlined"
                    onPress={onClose}
                    isDisabled={submitting}
                >
                    Abbrechen
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onPress={handleSave}
                    isDisabled={isNonEditable || submitting}
                    loading={submitting}
                >
                    Änderungseintrag einreichen
                </Button>
            </ModalActions>
        </Modal>
    );
};

export default WorkDayRecordEditModal;